/*
 * Waiver flow — `waiver.flow.*` (App-shell canon §6, FLEXIBLE).
 * Read → acknowledge per SECTION → typed signature → receipt, transcribed
 * from the canon §6 chrome templates + the DC source (App Shell.dc.html
 * waiverVals). Canon wins on conflict.
 *
 * Routes (canon §1.2): /get-ready/waiver/:eventId/doc-1..N, /sign, /receipt.
 * DOM ids (canon §1.3): waiver.root, waiver.head, waiver.doc.{docId},
 * waiver.sign, waiver.receipt, waiver.block.
 *
 * DATA (boardroom ruling E, BOARDROOM-2026-08-09-133534): the document set is
 * organiser-authored and settings-owned — consumed through the typed
 * WaiverTemplate seam (src/data/waiverTemplate.ts). No template ⇒ the honest
 * "not available yet" empty state (ruling A) — documents are NEVER hardcoded
 * (canon §6 must-not).
 *
 * SIGNED RECORDS: stored once at submit (waiverRecordStore + my-api's
 * submitWaiver when configured); re-opened via View they render READ-ONLY
 * from the record — counter SIGNED, inert consents, ticks derived from the
 * record (canon §6 signed-record rule).
 */
import { useEffect, useMemo, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useShellChrome } from '../../components/Shell'
import { useLocale } from '../../i18n/useLocale'
import { useDataOptional } from '../../data/DataProvider'
import { isOrgUnitUuid } from '../../data/orgBranding'
import { loadSession, nameFromClaims } from '../../auth/session'
import { getProfile } from '../../auth/cognitoProfile'
import { MyApiError, fetchMyHoldings, myApiBase, submitWaiver } from '../../data/myApi'
import {
  canonicalTemplateText, fetchWaiverTemplate, hashWaiverTemplate, type WaiverTemplate,
} from '../../data/waiverTemplate'
import {
  buildReceiptPrintHtml, declinedOptional, diagnoseBannerDegradation, docBlockText, docIntro, docOk, docsLine,
  fillParticipant, formatStamp, gateText, namedParty, receiptRows, recordTick, requiredLeft,
  resolveEventBannerUrl, sectionKey, signatureMatches, signBlockText, signChrome, signHint, signedOnLine,
  stepCounter, WAIVER_BANNER_DEGRADED_TAG,
  type ConsentState, type WaiverSubject,
} from './waiverModel'
import { loadWaiverRecord, storeWaiverRecord, type WaiverRecord } from './waiverRecordStore'
import { flowEntryFromSession, rememberFlowEntry } from '../../data/flowTicketSession'
import { bumpReadinessRefresh, receiptNextAction, type GetReadyStep } from '../../data/getReady'
import { guardianClaimForTicket } from '../../data/guardianClaim'
import { useGetReadyLiveOptional } from '../GetReady'

// ---------------------------------------------------------------------------
// Styles (canon §2 tokens; DC inline styles transcribed)
// ---------------------------------------------------------------------------

const h1Style: CSSProperties = {
  fontSize: 21, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 6px',
}
const introStyle: CSSProperties = {
  fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5, margin: '0 0 16px',
}
const eyebrowStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 9.5, letterSpacing: '.1em',
  textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: 4,
}
/** Section tag (BOARDROOM-2026-08-15-082717 / CMT-027 label half): three states —
 *  MUST ACCEPT (required, warn), OPTIONAL (an ACTUAL opt-in consent tick, faint),
 *  and ACCEPTED BY SIGNING (a read-then-sign clause, acknowledgement_required=false
 *  with NO consent tick — accepted by the single signature, NOT optional). Duncan:
 *  a non-acknowledgement clause must never read "optional". */
const tagStyle = (variant: 'must' | 'optional' | 'signed'): CSSProperties => ({
  fontFamily: 'var(--font-mono)', fontSize: 8.5, letterSpacing: '.09em',
  borderRadius: 4, padding: '2px 5px', flex: 'none',
  ...(variant === 'must'
    ? { color: 'var(--warn)', border: '1px solid rgba(230,193,90,.35)' }
    : variant === 'signed'
      ? { color: 'var(--success)', border: '1px solid rgba(18,196,172,.35)' }
      : { color: 'var(--text-faint)', border: '1px solid var(--hairline-14)' }),
})
/** NAMED PARTY chip (README §C): the organiser is the named party while the
 *  chrome stays Trumin. Accent = organiser IDENTITY colour, applied ONLY when
 *  the org unit's branding accent exists (useData().orgBranding — dormant
 *  today: the wire emits no org-unit UUID yet), else a NEUTRAL chip. Teal and
 *  amber stay reserved for state (README §Status colours). */
const namedPartyChipStyle = (accent: string | null): CSSProperties => ({
  fontFamily: 'var(--font-mono)', fontSize: 9, letterSpacing: '.09em',
  borderRadius: 5, padding: '3px 6px', flex: 'none',
  ...(accent
    ? { color: accent, border: `1px solid color-mix(in srgb, ${accent} 40%, transparent)` }
    : { color: 'var(--text-faint)', border: '1px solid var(--hairline-14)' }),
})
/** Ticked section card carries the teal border tint (canon §6 [visual-agent]). */
const sectionCardStyle = (on: boolean): CSSProperties => ({
  borderRadius: 14, background: 'var(--bg-frame)', marginBottom: 11, overflow: 'hidden',
  border: `1px solid ${on ? 'rgba(18,196,172,.45)' : 'var(--hairline-10)'}`,
})
/** Sub-clauses (1.2.x) indent visibly deeper than top-level clauses.
 *  Logical padding only — the indent mirrors under RTL (README §RTL). */
const clauseRowStyle = (sub: boolean): CSSProperties => ({
  display: 'grid', gridTemplateColumns: `${sub ? 42 : 30}px minmax(0,1fr)`,
  columnGap: 8, paddingBlockEnd: 9, paddingInlineStart: sub ? 14 : 0,
})
const clauseNumStyle: CSSProperties = {
  fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dim)', paddingTop: 2,
}
const clauseTextStyle = (sub: boolean): CSSProperties => ({
  fontSize: sub ? 12 : 12.5, lineHeight: 1.6, color: 'var(--text-secondary)',
})
const consentBoxStyle = (on: boolean): CSSProperties => ({
  width: 22, height: 22, flex: 'none', borderRadius: 7, display: 'grid',
  placeItems: 'center', fontSize: 12, fontWeight: 800,
  ...(on
    ? { background: 'var(--accent)', color: 'var(--accent-ink)' }
    : { background: 'transparent', border: '1.5px solid rgba(255,255,255,.28)', color: 'transparent' }),
})
const consentRowStyle = (on: boolean, inert: boolean): CSSProperties => ({
  display: 'grid', gridTemplateColumns: '22px minmax(0,1fr)', columnGap: 11,
  alignItems: 'start', width: '100%', textAlign: 'start', fontFamily: 'inherit',
  padding: '12px 14px', border: 0, borderTop: '1px solid var(--hairline-07)',
  cursor: inert ? 'default' : 'pointer',
  background: on ? 'rgba(18,196,172,.09)' : 'transparent',
})
const consentTextStyle = (on: boolean): CSSProperties => ({
  fontSize: 12.5, lineHeight: 1.5, fontWeight: 700,
  color: on ? 'var(--text)' : 'var(--text-softbtn)',
})
/** Signature input — Georgia italic 19px, 52px tap target (canon §2.2/§2.3);
 *  border idle hairline / match teal / mismatch red. */
const signBoxStyle = (tone: 'idle' | 'ok' | 'bad'): CSSProperties => ({
  width: '100%', minHeight: 52, borderRadius: 12, padding: '0 14px', fontSize: 19,
  fontFamily: 'var(--font-signature)', fontStyle: 'italic', background: 'var(--bg-frame)',
  color: 'var(--text)', outline: 'none', boxSizing: 'border-box',
  border: `1px solid ${tone === 'idle' ? 'var(--hairline-14)' : tone === 'ok' ? 'rgba(18,196,172,.55)' : 'var(--error-border)'}`,
})
const signHintStyle = (tone: 'idle' | 'ok' | 'bad'): CSSProperties => ({
  fontSize: 11.5, marginTop: 8,
  ...(tone === 'idle'
    ? { color: 'var(--text-faint)' }
    : tone === 'ok'
      ? { color: 'var(--success)', fontWeight: 700 }
      : { color: 'var(--error)', fontWeight: 700 }),
})
const backBtnStyle: CSSProperties = {
  flex: 'none', border: '1px solid var(--hairline-14)', background: 'transparent',
  color: 'var(--text-softbtn)', fontFamily: 'inherit', fontSize: 13.5, fontWeight: 700,
  cursor: 'pointer', minHeight: 48, borderRadius: 12, padding: '0 16px',
}
const ctaStyle = (ok: boolean): CSSProperties => ({
  flex: 1, border: 0, borderRadius: 12, minHeight: 48, background: 'var(--accent)',
  color: 'var(--accent-ink)', fontSize: 15, fontWeight: 800, fontFamily: 'inherit',
  ...(ok ? { cursor: 'pointer' } : { opacity: 0.35, cursor: 'not-allowed' }),
})
const outlineBtnStyle: CSSProperties = {
  width: '100%', border: '1px solid var(--hairline-16)', borderRadius: 12, minHeight: 46,
  background: 'transparent', color: 'var(--text)', fontSize: 13.5, fontWeight: 700,
  fontFamily: 'inherit', cursor: 'pointer',
}

// ---------------------------------------------------------------------------
// Live data hooks (real profile / template — nothing fabricated)
// ---------------------------------------------------------------------------

// CMT-2026-08-15-011: signed-waiver PDF brand assets (boardroom-provided, both
// verified live 200). The Trumin LOGO is the central-R2 wordmark — event-agnostic,
// so a plain constant. The EVENT BANNER now resolves via the CASCADE
// (BOARDROOM-134838 / MY-140426 — Duncan expected a banner on the Apex print,
// not just OnePulse; resolveEventBannerUrl in waiverModel.ts):
//   1. banner_desktop_url on settings' resolved waiver-template payload
//      (template.bannerDesktopUrl — the durable fix; not emitted yet), else
//   2. the org-branding resolved heroImageUrl the app already fetches/caches
//      (DataProvider → orgBranding — no duplicate request), else
//   3. this LEGACY per-event map entry (the original interim hardcode —
//      kept unchanged as the OnePulse backstop; do NOT add entries), else
//   4. no banner — graceful degradation to the organiser wordmark
//      (buildReceiptPrintHtml handles the absence).
// BOARDROOM-2026-08-28-163605 (Duncan): the corner brand is the ACTUAL Trumin
// LOGO (the official mark+wordmark lockup from the LOCKED trumin-logo asset
// pack) — NOT trumin-wordmark.png, which is the bare teal lettering with no
// mark and vanished on his black-and-white print preview.
const TRUMIN_LOGO_URL = 'https://assets.trumin.com/trumin-logo/assets/trumin-logo-static.png'
const EVENT_BANNER_URL: Record<string, string> = {
  'e5635953-24df-473f-9fad-3b0402bed777':
    'https://stage-manage.trumin.com/uploads/box-office/e5635953-24df-473f-9fad-3b0402bed777/banner-desktop-1785461166002.jpeg',
}

/** Resolved template: prop override (tests / future callers) else the
 *  settings seam. `null` resolution ⇒ honest empty state. */
function useTemplate(eventId: string, override: WaiverTemplate | null | undefined) {
  // CMT-2026-08-14-038: 'error' is now DISTINCT from 'empty'. Previously a 404
  // (no waiver published) AND any thrown failure (wrong event_id → 404-mapped,
  // 401/403 auth, 5xx, network, CORS) BOTH collapsed to the "isn't available
  // yet" empty state — hiding the real cause. A published waiver that failed to
  // load must NOT read as "the organiser hasn't published one". We also LOG the
  // event_id + failure so an event_id mismatch (the get-ready ticket's id vs the
  // settings-published id) is diagnosable from the browser console in one line.
  const [state, setState] = useState<'loading' | 'ready' | 'empty' | 'error'>(
    override !== undefined ? (override ? 'ready' : 'empty') : 'loading',
  )
  const [template, setTemplate] = useState<WaiverTemplate | null>(override ?? null)
  useEffect(() => {
    if (override !== undefined) return
    let cancelled = false
    fetchWaiverTemplate(eventId)
      .then((t) => {
        if (cancelled) return
        setTemplate(t)
        setState(t ? 'ready' : 'empty')
        if (!t) console.warn(`[waiver] no template resolved for event_id=${eventId} (settings returned 404 / no session / no base) — shows "not published"`)
      })
      .catch((err) => {
        // A THROW = the template exists-or-not is UNKNOWN (load failed), NOT a
        // confirmed "unpublished". Surface it as an error so the participant is
        // told to retry, not falsely told nothing is published.
        console.warn(`[waiver] resolve FAILED for event_id=${eventId}:`, err)
        if (!cancelled) setState('error')
      })
    return () => { cancelled = true }
  }, [eventId, override])
  return { state, template }
}

/** BOARDROOM-142033 (Duncan launch decision): when false, the waiver signature
 *  name-match does NOT block signing (the account authenticates the signer; the
 *  typed name + acceptance are still captured). Set true to re-enable a
 *  corrected match once the root-cause fix (whitespace-collapse + diacritic-fold
 *  + participant-vs-buyer name source) ships. Module const = clean re-enable
 *  without touching the sign logic. */
const WAIVER_NAME_MATCH_BLOCKS = false

// BOARDROOM-174911 — the my-api ticket target for the waiver submit must survive
// a hard refresh / deep link (React Router drops location.state on reload). We
// remember the entry ticketId per-event in sessionStorage so the server persist
// (submitWaiver → waiver_complete) still fires, and resolve it from live holdings
// as a final bulletproof fallback for ANY entry path (see resolveWaiverTicketId).
// The storage rail is now the SHARED per-flow helper (src/data/flowTicketSession
// — extracted so QuestionnaireFlow's framing recovery rides the same rail);
// these thin wrappers keep the waiver call sites and semantics unchanged.
function rememberWaiverTicket(eventId: string, ticketId: string): void {
  rememberFlowEntry('waiver', eventId, { ticketId })
}
function waiverTicketFromSession(eventId: string): string | undefined {
  return flowEntryFromSession('waiver', eventId)?.ticketId
}

// nameFromClaims moved to src/auth/session.ts (Get-ready card-identity slice,
// Duncan UI order BOARDROOM-135429): the waiver signature prefill and the
// Get-ready "For {name}" subject line now share the ONE claims-name source.

/** Real account-holder name — the expected signature. Resolved from a robust
 *  authoritative chain so it aligns with get-ready readiness (BOARDROOM-2026-08-29-032706
 *  F1): the two profile-completion signals disagreed because this hook resolved
 *  the name ONLY via the Cognito getProfile read and reported 'error' whenever
 *  that call failed or returned empty — falsely gating waiver signing for a
 *  participant whose profile IS complete. Resolution order:
 *    1. the override prop (tests / future callers),
 *    2. getProfile(accessToken) given_name+family_name (Cognito read),
 *    3. FALLBACK to the session ID-token claims (given_name+family_name, then name).
 *  Only when NO name is available from ANY source ⇒ 'error' (the honest
 *  genuinely-no-name gate is preserved). Never a fabricated name. */
function useProfileName(override: string | undefined) {
  const [name, setName] = useState<string | null>(override ?? null)
  const [state, setState] = useState<'loading' | 'ready' | 'error'>(override ? 'ready' : 'loading')
  useEffect(() => {
    if (override) return
    const session = loadSession()
    if (!session) {
      setState('error')
      return
    }
    let cancelled = false
    const claimName = nameFromClaims(session.claims as Record<string, unknown>)
    const settle = (resolved: string) => {
      if (cancelled) return
      if (resolved) {
        setName(resolved)
        setState('ready')
      } else setState('error')
    }
    getProfile(session.accessToken)
      .then((p) => {
        // Profile read first, then the verified session claims — so a
        // getProfile that fails OR returns empty still resolves the name when
        // the completed-profile session token carries it (aligns with readiness).
        settle([p.givenName, p.familyName].filter(Boolean).join(' ').trim() || claimName)
      })
      // getProfile threw (auth/network/CORS) — fall back to the authoritative claims.
      .catch(() => settle(claimName))
    return () => { cancelled = true }
  }, [override])
  return { name, state }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function WaiverFlow({
  template: templateProp, participantName, subject: subjectProp, steps: stepsProp, ticketPassId: ticketPassIdProp,
}: {
  /** Test/caller override of the settings-seam template. `null` = explicitly
   *  no template (empty state); `undefined` = resolve via the seam. */
  template?: WaiverTemplate | null
  /** Test override of the real Cognito profile name (the expected signature). */
  participantName?: string
  /** Signing-subject override (tests / callers). When ABSENT the flow
   *  resolves the subject itself (child-holder launch-gate slice — the
   *  guardian entry is now WIRED): Get-ready's per-holding route state
   *  (holderKind/holderName), else the caller's live holdings; a child
   *  holding ⇒ the guardian signs for the named child. Unresolved = self. */
  subject?: WaiverSubject
  /** Test override of the live Get-ready step rows for this event (the receipt's
   *  next-action source). Production reads them from the shared live provider. */
  steps?: GetReadyStep[]
  /** Test override of the backing ticket id (the wallet/QR deep-link target). */
  ticketPassId?: string
}) {
  const { isMobile } = useShellChrome()
  const { t, dir } = useLocale()
  // Back chevron encodes direction — swapped in code, never CSS-mirrored
  // (README §RTL).
  const backChev = dir === 'rtl' ? '›' : '‹'
  const navigate = useNavigate()
  const location = useLocation()
  const { eventId = '', stepSlug = '' } = useParams()

  // Organiser branding accent for the NAMED PARTY chip — resolved from the
  // central store via the org unit's REAL UUID only (synthesised ids are never
  // sent); absent/unresolved ⇒ neutral chip, never an invented colour.
  const data = useDataOptional()
  const waiverEvent = eventId ? data?.eventById(eventId) : undefined
  const orgUnitId = waiverEvent?.organiser.id
  const orgUnitIsUuid = !!orgUnitId && isOrgUnitUuid(orgUnitId)
  // Raw DataProvider branding cache entry, kept UN-collapsed for the leg-4
  // beacon (BOARDROOM-2026-09-05-101523): undefined = unfetched/failed,
  // null = org-unit 404, record = resolved (possibly heroless). The old
  // `?? null` collapse made a failed read indistinguishable from proven
  // absence — the silent fall-through Duncan kept hitting.
  const orgBrandingRec = orgUnitIsUuid ? data?.orgBranding(orgUnitId) : undefined
  const orgAccent = orgBrandingRec?.organiserAccent ?? null
  // Org-branding HERO for the print-banner cascade tier 2 — the SAME
  // DataProvider fetch/cache the accent above (and Tickets) reads; no extra
  // request. Outside a provider, or when branding is unfetched / failed /
  // 404'd / heroless, this is null and the cascade falls through — a broken
  // or absent branding read can never block printing.
  const orgHeroUrl = orgBrandingRec?.heroImageUrl ?? null

  // BOARDROOM-174911 (+ BOARDROOM-2026-08-17-150200 item 1): capture the entry
  // ticketId into sessionStorage the moment it is present — from route state OR
  // a ?ticketId= query param (deep links) — so a hard refresh (which drops React
  // Router's location.state) and in-flow navigation (which drops the query) do
  // NOT lose the server target for the waiver submit.
  useEffect(() => {
    const entry =
      (location.state as { ticketId?: string } | null)?.ticketId ??
      (new URLSearchParams(location.search).get('ticketId') || undefined)
    if (entry && eventId) rememberWaiverTicket(eventId, entry)
  }, [eventId, location.state, location.search])

  const { state: tplState, template } = useTemplate(eventId, templateProp)
  const { name: profileName, state: nameState } = useProfileName(participantName)

  // GUARDIAN SUBJECT (child-holder launch-gate slice — wires the dormant
  // WaiverSubject guardian entry). Resolution order:
  //  1. the subject prop (tests / callers),
  //  2. route state from Get-ready's per-holding card (holderKind +
  //     holderName ride alongside ticketId in onStepAction),
  //  2b. the device-local GUARDIAN-CLAIM record for the entry ticket (S2
  //     slice, BOARDROOM-2026-09-03-052111 / CMT-2026-09-03-020): a hard
  //     refresh drops route state, and the wire's holdings may carry no
  //     child identity for a guardian-claimed seat — the record still names
  //     the child, so the guardian framing survives a reload,
  //  3. the caller's live holdings (deep link / hard refresh, where route
  //     state is gone): the holding matching the entry ticketId — kind
  //     'child' ⇒ the guardian signs for the named child.
  // A guardian subject requires a NAMED child (wire holderName / the claim
  // record) — an unnamed or unresolved subject stays 'self', exactly the
  // pre-slice behaviour; never invented.
  const routeHolder = location.state as { holderKind?: string; holderName?: string | null } | null
  const routeSubject: WaiverSubject | null =
    routeHolder?.holderKind === 'child' && (routeHolder.holderName ?? '').trim()
      ? { kind: 'guardian', who: (routeHolder.holderName ?? '').trim() }
      : null
  const entryTicketForSubject =
    (location.state as { ticketId?: string } | null)?.ticketId ??
    (new URLSearchParams(location.search).get('ticketId') || undefined) ??
    (eventId ? waiverTicketFromSession(eventId) : undefined)
  const guardianClaimRec = entryTicketForSubject ? guardianClaimForTicket(entryTicketForSubject) : null
  const recordSubject: WaiverSubject | null = guardianClaimRec?.childName
    ? { kind: 'guardian', who: guardianClaimRec.childName }
    : null
  const [holdingSubject, setHoldingSubject] = useState<WaiverSubject | null>(null)
  const needsHoldingSubject = !subjectProp && !routeSubject && !recordSubject
  // ONE holdings read per event entry (in-flow navigation drops the query
  // param and would otherwise re-fire the effect) — the subject cannot
  // change mid-flow, and the persist tests hold the submit path to zero
  // holdings fallbacks.
  const holdingSubjectTried = useRef<string | null>(null)
  useEffect(() => {
    if (!needsHoldingSubject || holdingSubjectTried.current === eventId) return
    const base = myApiBase()
    const session = loadSession()
    if (!base || !session || !eventId) return
    holdingSubjectTried.current = eventId
    // Same entry-ticket candidates as the submit resolution (route state /
    // ?ticketId= / the sessionStorage entry capture) — the holdings read is
    // the authority on WHO that ticket is for.
    const entryTicket =
      (location.state as { ticketId?: string } | null)?.ticketId ??
      (new URLSearchParams(location.search).get('ticketId') || undefined) ??
      waiverTicketFromSession(eventId)
    let cancelled = false
    fetchMyHoldings(session.idToken, base)
      .then((holdings) => {
        if (cancelled) return
        const mine = holdings.find(
          (h) => h.eventId === eventId && h.state !== 'revoked' && (entryTicket ? h.ticketId === entryTicket : true),
        )
        const childName = (mine?.holderName ?? '').trim()
        if (mine?.kind === 'child' && childName) {
          setHoldingSubject({ kind: 'guardian', who: childName })
        }
      })
      .catch(() => { /* unresolved ⇒ self (pre-slice behaviour), never guessed */ })
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsHoldingSubject, eventId, location.state, location.search])
  const subject: WaiverSubject = subjectProp ?? routeSubject ?? recordSubject ?? holdingSubject ?? { kind: 'self' }

  // Consent scratch — per-SECTION only, scoped to this event; reset when the
  // event changes and on submit ("never carried between agreements", §6).
  const [consents, setConsents] = useState<ConsentState>({})
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const [typed, setTyped] = useState('')
  const [scopedEvent, setScopedEvent] = useState(eventId)
  if (scopedEvent !== eventId) {
    setScopedEvent(eventId)
    setConsents({})
    setCollapsed({})
    setTyped('')
  }

  const [submitState, setSubmitState] = useState<'idle' | 'submitting' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<string | null>(null)
  // Bumped after storeWaiverRecord so the record re-loads.
  const [recVersion, setRecVersion] = useState(0)

  // Receipt next-action source of truth: the SAME live Get-ready step rows the
  // home derives from (never a hardcode). Read from the shared provider in
  // production; a test/caller prop overrides it. The optional reader returns
  // null outside a provider (e.g. component tests) — then the prop is the only
  // source, and its absence yields the honest "ticket pending" state.
  const live = useGetReadyLiveOptional()
  // PER-HOLDING match (child-holder slice): when the entry carried a ticketId,
  // the receipt's next-action steps must come from THAT holding's card — a
  // guardian's own card and a child card share the eventId. No entry ticket ⇒
  // the first event card (pre-slice behaviour). The sessionStorage entry
  // capture rides here too (review P2-1): a hard refresh / deep link on the
  // CHILD waiver must not fall back to the wrong same-event holding.
  const entryTicketId =
    (location.state as { ticketId?: string } | null)?.ticketId ??
    (new URLSearchParams(location.search).get('ticketId') || undefined) ??
    (eventId ? waiverTicketFromSession(eventId) : undefined)
  const liveEvent =
    live?.events.find((e) => e.id === eventId && (entryTicketId ? e.ticketId === entryTicketId : true))
    ?? live?.events.find((e) => e.id === eventId)
    ?? null
  const effectiveSteps = stepsProp ?? liveEvent?.steps ?? null
  const ticketPassId =
    ticketPassIdProp ?? entryTicketId ?? liveEvent?.ticketId ?? (eventId ? live?.ticketByEvent[eventId] : undefined)

  // Signed record — TICKET-SCOPED since review P1-1 (child-holder slice): the
  // event-keyed v1 record made a guardian's own signature render the CHILD's
  // waiver as signed (dead submit, waiver_complete never true, QR never
  // minted) and leaked the child's record onto the guardian's own waiver in
  // the reverse order. The v2 read is keyed to THIS entry's ticket; the
  // legacy v1 event-keyed record is accepted ONLY where it is unambiguous —
  // a SELF subject on a single-holding event (with live topology unknown, a
  // single holding is assumed: exactly the pre-slice population, since dual
  // holdings only exist once child holders do).
  const dualHolding = live ? live.events.filter((e) => e.id === eventId).length > 1 : false
  const record = useMemo(
    () => (eventId
      ? loadWaiverRecord(eventId, {
          ticketId: ticketPassId,
          acceptLegacy: subject.kind !== 'guardian' && !dualHolding,
        })
      : null),
    [eventId, recVersion, ticketPassId, subject.kind, dualHolding],
  )
  const signed = record !== null

  // --- flow chrome geometry (same flow frame as the profile wizard: the
  // shell content padding is cancelled so the flow fills edge-to-edge) ---
  const pad = isMobile
    ? { margin: '-18px -16px -26px', height: 'calc(100% + 44px)' }
    : { margin: '-26px -30px -36px', height: 'calc(100% + 62px)' }
  const headPad = isMobile ? '12px 16px 10px' : '18px 26px 12px'
  const bodyPad = isMobile ? '16px 16px 20px' : '20px 26px 24px'
  const footPad = isMobile ? '10px 16px 12px' : '14px 26px 16px'
  const blockPadX = isMobile ? 16 : 26

  const exitFlow = () => navigate('/get-ready')
  const go = (slug: string) => navigate(`/get-ready/waiver/${eventId}/${slug}`, { state: location.state })

  // ---- template unresolved: loading / honest empty state (rulings A + E) ----
  if (tplState !== 'ready' || !template) {
    // Non-canon operational chrome — canon §6 defines no empty state; the
    // honest-empty posture (ruling A) requires one rather than invented docs.
    const routeState = location.state as { eventName?: string } | null
    return (
      <div id="waiver.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div id="waiver.head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
            <button onClick={exitFlow} aria-label={t('flow.close')} style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}>{backChev}</button>
            <span data-testid="waiver-context" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{routeState?.eventName ?? t('waiver.fallbackContext')}</span>
          </div>
        </div>
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
          {tplState === 'loading' ? (
            <p role="status" style={introStyle}>{t('waiver.loading')}</p>
          ) : tplState === 'error' ? (
            // CMT-038: load FAILED — do NOT claim it's unpublished. Tell the
            // participant the truth (couldn't load) and let them retry.
            <div data-testid="waiver-error">
              <h1 style={h1Style}>{t('waiver.error.title')}</h1>
              <p style={introStyle}>{t('waiver.error.body')}</p>
              <button onClick={() => window.location.reload()} style={{ ...ctaStyle(true), marginTop: 12 }}>{t('waiver.error.retry')}</button>
            </div>
          ) : (
            <div data-testid="waiver-empty">
              <h1 style={h1Style}>{t('waiver.empty.title')}</h1>
              <p style={introStyle}>{t('waiver.empty.body')}</p>
            </div>
          )}
        </div>
        <div style={{ flex: 'none', display: 'flex', padding: footPad, borderTop: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
          {/* ONE-green sweep (BOARDROOM-2026-09-05-121609 item 7): back/exit
              navigation is never primary — on the ERROR state `Try again` is
              the screen's single green; loading/empty have no next step, so
              zero greens. The exit stays, quiet (outline). */}
          <button onClick={exitFlow} style={{ ...outlineBtnStyle, minHeight: 48 }}>{t('waiver.backToGetReady')}</button>
        </div>
      </div>
    )
  }

  // ---- resolved template: route → step ----
  const docs = template.documents
  const NDOCS = docs.length
  const docMatch = /^doc-(\d+)$/.exec(stepSlug)
  const docIndex = docMatch ? Number(docMatch[1]) - 1 : -1
  const onDoc = docIndex >= 0 && docIndex < NDOCS
  const onSign = stepSlug === 'sign'
  const onReceipt = stepSlug === 'receipt'

  // Guards: unknown slug → doc-1; signed record cannot be re-signed (sign →
  // receipt); the receipt is a STORED record, not a regenerated view — no
  // record ⇒ nothing to show (→ doc-1).
  if (!onDoc && !onSign && !onReceipt) return <Navigate to={`/get-ready/waiver/${eventId}/doc-1`} replace />
  if (onSign && signed) return <Navigate to={`/get-ready/waiver/${eventId}/receipt`} replace />
  if (onReceipt && !signed) return <Navigate to={`/get-ready/waiver/${eventId}/doc-1`} replace />

  const who = subject.kind === 'guardian' ? subject.who : (profileName ?? '')
  // The expected signature is ALWAYS the signer's own profile name — the
  // guardian signs their own name, never the child's (canon §6).
  const expected = profileName ?? ''

  const doc = onDoc ? docs[docIndex] : null
  const reqLeft = doc ? requiredLeft(doc, consents) : 0
  // A signed record is read-only — browsing forward is never gated (§6).
  const curDocOk = doc ? signed || docOk(doc, consents) : true
  // BOARDROOM-142033 (Duncan launch decision): the signature NAME-MATCH is
  // NON-BLOCKING. The account already authenticates the signer + the typed name
  // + acceptance are still captured — the exact-name-match was a UX gate only,
  // and it was rejecting legitimate participants (internal double-spaces,
  // diacritics, or a blank/skeletal profileName all fail the strict
  // trim+lowercase compare). With the flag OFF a signed-in participant can sign
  // with ANY non-empty typed name. Flip WAIVER_NAME_MATCH_BLOCKS back to true to
  // re-enable a (corrected) match later — the field + normalise logic are kept.
  const signOk =
    typed.trim() !== '' &&
    (!WAIVER_NAME_MATCH_BLOCKS || (expected !== '' && signatureMatches(typed, expected)))
  const stepIndex = onDoc ? docIndex : NDOCS
  const stepnum = stepCounter(stepIndex, NDOCS, signed || onReceipt, t)
  const progressPct = signed || onReceipt ? 100 : ((stepIndex + 1) / (NDOCS + 1)) * 100

  const ok = onDoc ? curDocOk : onSign ? signOk : true
  const showBlock = !signed && !onReceipt && !ok

  const back = () => {
    if (onSign) go(`doc-${NDOCS}`)
    else if (docIndex <= 0) exitFlow()
    else go(`doc-${docIndex}`)
  }
  const continueDoc = () => {
    if (!curDocOk || !doc) return
    if (docIndex < NDOCS - 1) go(`doc-${docIndex + 2}`)
    else go('sign')
  }

  const submit = async () => {
    if (!signOk || signed || submitState === 'submitting') return
    setSubmitState('submitting')
    setSubmitError(null)
    const now = new Date()
    const rec: WaiverRecord = {
      eventId,
      participant: who,
      guardian: subject.kind === 'guardian',
      signedBy: typed.trim(),
      when: formatStamp(now),
      signedAtIso: now.toISOString(),
      docs: docs.map((d) => ({ title: d.title, version: d.version })),
      declined: declinedOptional(docs, consents),
    }
    try {
      // Durable record on my-api when the seam is configured. ticketId rides
      // in from the Get-ready entry (route state); a deep link without it has
      // no server target — the local record still captures the signing.
      // TODO(my-api): the waiver contract carries ONE templateVersion/hash —
      // multi-doc fields need a contract amendment (boardroom-routed).
      const base = myApiBase()
      const session = loadSession()
      // BOARDROOM-174911 ROOT CAUSE + BULLETPROOF FIX (hardened per
      // BOARDROOM-2026-08-17-150200 item 1): the my-api ticket target used to
      // come ONLY from route state, which React Router DROPS on a hard refresh
      // (and a deep link never has it). When missing, the server persist was
      // SILENTLY skipped while the local "signed" record was still stored — so
      // the participant saw a signed waiver but server waiver_complete stayed
      // false → get-ready showed it outstanding AND the QR never minted. Resolve
      // the ticketId from FIVE sources so no entry path can lose it — route
      // state, ?ticketId= query param, sessionStorage (entry capture), the live
      // Get-ready context's ticket for this event, and finally the caller's own
      // holdings — and if it is STILL unresolvable, surface an error instead of
      // a false local-only sign.
      let ticketId =
        (location.state as { ticketId?: string } | null)?.ticketId ??
        (new URLSearchParams(location.search).get('ticketId') || undefined) ??
        waiverTicketFromSession(eventId) ??
        ticketPassId
      if (base && session && !ticketId) {
        // Final, universal fallback: the caller's OWN live holding for this event
        // (works for ANY entry path — fresh tab, deep link, post-refresh).
        try {
          const holdings = await fetchMyHoldings(session.idToken, base)
          const mine = holdings.find((h) => h.eventId === eventId && h.state !== 'revoked')
          if (mine) { ticketId = mine.ticketId; rememberWaiverTicket(eventId, ticketId) }
        } catch { /* fall through to the error below — never a silent skip */ }
      }
      if (base && session) {
        if (!ticketId) {
          // Seam configured but genuinely no ticket target (e.g. an unassigned
          // team seat) — do NOT store a local-only "signed" record that masks an
          // un-persisted waiver. Surface it so the user resolves their ticket.
          throw new Error(
            "We couldn’t link your signature to your ticket. Please re-open the waiver from your Get-ready page and sign again.",
          )
        }
        await submitWaiver(session.idToken, base, ticketId, {
          signerName: rec.signedBy,
          consented: true,
          templateVersion: docsLine(rec.docs),
          templateHash: hashWaiverTemplate(canonicalTemplateText(template)),
          signedAt: rec.signedAtIso,
        })
      }
      // TICKET-SCOPED record (review P1-1): stamp the resolved ticket onto the
      // record so it keys per-ticket (v2) — a guardian's dual holdings (own +
      // child seat, same event) each carry their OWN signed record. A
      // seamless/local-only sign with no resolved ticket keeps the legacy
      // event key (pre-slice behaviour).
      storeWaiverRecord(ticketId ? { ...rec, ticketId } : rec)
      // Scratch reset — consents are never carried between agreements (§6).
      setConsents({})
      setTyped('')
      setSubmitState('idle')
      setRecVersion((v) => v + 1)
      // The waiver step is now done server-side — signal the live Get-ready
      // subscription to re-read readiness so the receipt's next-action (and the
      // home) reflect the freshly-signed state rather than a stale snapshot.
      bumpReadinessRefresh()
      go('receipt')
    } catch (e) {
      setSubmitState('error')
      // Prefer the SERVER's own plain copy when the API sent one (the house
      // pattern — AssignFlow/getReady read e.serverMessage the same way): a
      // 422 like the minor-signer refusal must reach the user verbatim, never
      // as an opaque "waiver submit failed (422)".
      setSubmitError(
        e instanceof MyApiError && e.serverMessage
          ? e.serverMessage
          : e instanceof Error ? e.message : t('waiver.submitFailed'),
      )
    }
  }

  const blockText = onDoc
    ? docBlockText(reqLeft, t)
    : WAIVER_NAME_MATCH_BLOCKS
      ? signBlockText(typed, expected, t)
      : t('waiver.block.sign.empty') // non-blocking (BOARDROOM-142033): only the empty case blocks
  const sc = signChrome(subject, who, t)
  // BOARDROOM-142033: with the name-match non-blocking, never show the red
  // "must match … exactly" mismatch hint — a typed name is always acceptable.
  const hint: { text: string; tone: 'idle' | 'ok' | 'bad' } = WAIVER_NAME_MATCH_BLOCKS
    ? signHint(typed, expected, t)
    : { text: typed.trim() ? t('waiver.sign.hint.captured') : t('waiver.block.sign.empty'), tone: typed.trim() ? 'ok' : 'idle' }

  // Receipt: the two options — the printable signed copy + the context-aware
  // next action (a further Get-ready step / the issued ticket / a pending
  // message when neither applies), derived from the live step rows.
  const nextAction = record ? receiptNextAction(eventId, effectiveSteps, ticketPassId) : null

  /**
   * "Download the signed PDF" — open a print window of a self-contained, styled
   * receipt built from the STORED record (+ the on-screen template header) and
   * invoke the browser's print-to-PDF. No network, no CDN library (offline/CSP
   * posture). Nothing is fabricated — the print doc renders only the record.
   */
  const downloadSignedPdf = () => {
    if (!record) return
    // CMT-032: pass the full acknowledged document tree + the named party so the
    // print doc carries the ACTUAL legal text the signer agreed to (branded),
    // not just the receipt stub.
    // CMT-011: event banner across the top + Trumin logo bottom-right per
    // page. Resolved via the CASCADE (BOARDROOM-134838): resolved-template
    // payload banner > org-branding hero (shared cache) > legacy map >
    // wordmark degradation. All inputs are already-resolved values — no
    // fetch at print time, so a missing/broken banner never blocks printing.
    const eventBannerUrl = resolveEventBannerUrl({
      templateBannerUrl: template.bannerDesktopUrl,
      brandingHeroUrl: orgHeroUrl,
      legacyBannerUrl: EVENT_BANNER_URL[eventId],
    })
    // Leg-4 degradation BEACON (BOARDROOM-2026-09-05-101523): the wordmark
    // fall-through used to be SILENT, so a resolution failure (event missing
    // from the purchaser-keyed orders wire — the claim/reassign holder's
    // session; no organiserId UUID on the wire; branding read failed/unlanded)
    // read exactly like "this event has no banner" — and the complaint
    // recurred. Warn with a stable grep tag when leg 4 is hit for a
    // DIAGNOSABLE failure; stay silent on PROVEN absence (branding resolved,
    // no hero — genuinely unconfigured). Diagnosis only: printing proceeds
    // identically either way.
    if (!eventBannerUrl) {
      const reason = diagnoseBannerDegradation({
        hasDataContext: data != null,
        // Orders-wire readiness rides into the diagnosis (review round 2):
        // while the wire is still loading/unreadable an eventById miss is NOT
        // evidence the event is off the wire — the beacon then reports the
        // wire state itself instead of a false 'event-not-in-orders-wire'.
        ordersWireState: data?.liveState ?? 'unconfigured',
        eventFound: waiverEvent != null,
        orgUnitIsUuid,
        branding: orgBrandingRec,
      })
      if (reason) {
        console.warn(
          `${WAIVER_BANNER_DEGRADED_TAG} signed-PDF banner degraded to the wordmark for event_id=${eventId} — `
          + `reason=${reason} (org_unit_id=${orgUnitId ?? 'unresolved'}). Banner data may exist upstream but was `
          + `not resolvable at print time; printing proceeds. BOARDROOM-2026-09-05-101523`,
        )
      }
    }
    const html = buildReceiptPrintHtml(record, {
      event: template.event,
      organiser: template.organiser,
      documents: template.documents,
      who,
      eventBannerUrl,
      truminLogoUrl: TRUMIN_LOGO_URL,
    })
    const w = window.open('', '_blank')
    if (!w) return // jsdom / blocked popup — nothing to print.
    w.document.open()
    w.document.write(html)
    w.document.close()
    w.focus()
    // Prefer printing once the remote brand images (banner + logo) have loaded so
    // they aren't missing from the PDF (CMT-011). Fall back to a longer timeout if
    // load never fires (cached/errored) — 600ms covers the ~230KB banner + ~137KB
    // logo; still prints if the images fail (they degrade to nothing, not a hang).
    const doPrint = () => { try { w.print() } catch { /* user cancelled / unsupported */ } }
    let printed = false
    const once = () => { if (!printed) { printed = true; doPrint() } }
    try {
      const imgs = Array.from(w.document.images)
      const pending = imgs.filter((im) => !im.complete)
      if (pending.length === 0) { setTimeout(once, 150) }
      else {
        let left = pending.length
        pending.forEach((im) => {
          const tick = () => { if (--left <= 0) once() }
          im.addEventListener('load', tick, { once: true })
          im.addEventListener('error', tick, { once: true })
        })
        setTimeout(once, 1500) // hard fallback so it always prints
      }
    } catch { setTimeout(once, 600) }
  }

  const goNext = () => {
    if (!nextAction || nextAction.kind === 'pending') return
    // Step surfaces (heat especially) expect the backing ticket in route state,
    // mirroring Get-ready's onStepAction; harmless for the profile steps.
    // GUARDIAN FRAMING RIDES ALONG (waiver→questionnaire framing loss): this
    // flow has ALREADY resolved the signing subject (prop > route state >
    // claim record > live holdings — see the resolution above), so the next
    // step gets the same holderKind/holderName carry Get-ready would have sent
    // and keeps the child framing instead of degrading to the self render. A
    // self subject adds nothing — 'self' is this flow's unresolved fallback,
    // not a positive wire assertion, so it is never forwarded as one.
    navigate(nextAction.path, {
      state: {
        ticketId: ticketPassId,
        eventName: template.event,
        ...(subject.kind === 'guardian' ? { holderKind: 'child', holderName: subject.who } : {}),
      },
    })
  }

  return (
    <div id="waiver.root" style={{ ...pad, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* HEADER — back chevron (direction-swapped in code), event context,
          Step {n} of {N} / SIGNED, 5px progress (README §Flows shared chrome) */}
      <div id="waiver.head" style={{ flex: 'none', padding: headPad, borderBottom: '1px solid var(--hairline-07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
          <button onClick={exitFlow} aria-label={t('flow.close')} style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 13, fontWeight: 700, cursor: 'pointer', minHeight: 44, padding: 0 }}>{backChev}</button>
          <span data-testid="waiver-context" style={{ fontSize: 12, color: 'var(--text-muted)', flex: 1, minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{template.event}</span>
          <span data-testid="waiver-stepnum" style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-muted)', flex: 'none' }}>{stepnum}</span>
        </div>
        <div style={{ height: 5, borderRadius: 20, background: 'var(--progress-track)', overflow: 'hidden', marginTop: 10 }}>
          <div data-testid="waiver-progress" style={{ height: '100%', borderRadius: 20, background: 'var(--accent)', width: `${progressPct}%` }} />
        </div>
      </div>

      {/* BODY */}
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto', padding: bodyPad }}>
        {/* ---- DOC SCREEN ---- */}
        {onDoc && doc && (
          <div id={`waiver.doc.${doc.id}`}>
            <div data-testid="waiver-eyebrow" style={eyebrowStyle}>{t('waiver.eyebrow.doc', { n: docIndex + 1, m: NDOCS, version: doc.version })}</div>
            <h1 data-testid="waiver-title" style={h1Style}>{fillParticipant(doc.title, who)}</h1>
            <p style={introStyle}>{docIntro(doc, subject, template.organiser, signed, t)}</p>

            <div data-testid="waiver-named-party" style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <span data-testid="waiver-named-party-chip" data-accent={orgAccent ?? undefined} style={namedPartyChipStyle(orgAccent)}>{t('waiver.namedParty')}</span>
              <span style={{ fontSize: 11.5, color: 'var(--text-muted)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{namedParty(template)}</span>
            </div>

            {/* Gate line: {N} still to accept → ✓ all accepted; read-only
                re-open swaps to the signed banner (canon §6). */}
            <div
              data-testid="waiver-gate"
              style={{
                fontSize: 11.5, margin: '2px 0 4px',
                ...(signed || curDocOk ? { color: 'var(--success)', fontWeight: 700 } : { color: 'var(--text-muted)' }),
              }}
            >
              {signed && record ? t('waiver.gate.signed', { name: record.signedBy, when: record.when }) : gateText(reqLeft, t)}
            </div>

            {doc.sections.map((s, i) => {
              const k = sectionKey(doc.id, i)
              const on = signed && record ? recordTick(s, record.declined) : !!consents[k]
              const open = !collapsed[k]
              return (
                <div key={k} data-testid="waiver-section" data-section={s.name} style={sectionCardStyle(on)}>
                  <div style={{ padding: '12px 14px 4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10, color: 'var(--text-dim)', flex: 'none' }}>{i + 1}</span>
                      <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 800, letterSpacing: '-0.01em' }}>{fillParticipant(s.name, who)}</span>
                      {(() => {
                        // required → MUST ACCEPT; not-required WITH a consent tick →
                        // genuine OPTIONAL opt-in; not-required WITHOUT a tick →
                        // read-then-sign clause = ACCEPTED BY SIGNING (never "optional").
                        const variant: 'must' | 'optional' | 'signed' = s.required ? 'must' : s.consent ? 'optional' : 'signed'
                        const label = t(variant === 'must' ? 'waiver.tag.must' : variant === 'optional' ? 'waiver.tag.optional' : 'waiver.tag.signed')
                        return <span data-testid="waiver-section-tag" data-variant={variant} style={tagStyle(variant)}>{label}</span>
                      })()}
                    </div>
                    {open && s.clauses.map((c) => (
                      <div key={c.num} data-testid="waiver-clause" data-num={c.num} style={clauseRowStyle(!!c.sub)}>
                        <span style={clauseNumStyle}>{c.num}</span>
                        <span style={clauseTextStyle(!!c.sub)}>{fillParticipant(c.text, who)}</span>
                      </div>
                    ))}
                    {s.note && (
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', lineHeight: 1.5, paddingBottom: 9 }}>{s.note}</div>
                    )}
                    <button
                      data-testid="waiver-section-toggle"
                      onClick={() => setCollapsed((c) => ({ ...c, [k]: !c[k] }))}
                      style={{ border: 0, background: 'transparent', color: 'var(--text-muted)', fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, cursor: 'pointer', padding: '2px 0 8px' }}
                    >
                      {open ? t('waiver.clauses.hide') : t('waiver.clauses.show', { n: s.clauses.length })}
                    </button>
                  </div>
                  {/* One tick PER SECTION — inert on a signed record (§6). Shown
                      only when the organiser authored a consent row; a
                      typed-signature template (no per-section consent) is
                      accepted by the signature alone, so read-only sections show
                      no tick. */}
                  {s.consent && (
                    <button
                      data-testid="waiver-consent"
                      data-section={s.name}
                      aria-pressed={on}
                      aria-disabled={signed || undefined}
                      onClick={signed ? undefined : () => setConsents((c) => ({ ...c, [k]: !c[k] }))}
                      style={consentRowStyle(on, signed)}
                    >
                      <span style={consentBoxStyle(on)}>{on ? '✓' : ''}</span>
                      <span style={consentTextStyle(on)}>{fillParticipant(s.consent, who)}</span>
                    </button>
                  )}
                </div>
              )
            })}

            {/* Read-only re-open: an unmistakable signature block at the foot of
                the agreement (CMT-2026-08-13-121331) — the signer's own name +
                "Signed on {date} at {time}" from the stored record, so the
                participant can see they HAVE signed. */}
            {signed && record && (
              <div
                data-testid="waiver-signed-block"
                style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid var(--hairline-10)' }}
              >
                <div style={eyebrowStyle}>{t('waiver.eyebrow.signature')}</div>
                <div style={{ fontFamily: 'var(--font-signature)', fontStyle: 'italic', fontSize: 20, color: 'var(--text)' }}>{record.signedBy}</div>
                <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4, fontWeight: 700 }}>{signedOnLine(record, t)}</div>
              </div>
            )}
          </div>
        )}

        {/* ---- SIGN SCREEN ---- */}
        {onSign && (
          <div id="waiver.sign">
            <div data-testid="waiver-eyebrow" style={eyebrowStyle}>{t('waiver.eyebrow.sign')}</div>
            <h1 data-testid="waiver-title" style={h1Style}>{sc.heading}</h1>
            <p style={introStyle}>{sc.intro}</p>
            {nameState === 'loading' ? (
              <p role="status" style={introStyle}>{t('waiver.profileLoading')}</p>
            ) : nameState === 'error' ? (
              // CMT-2026-08-15 (Duncan launch blocker, Option B): the signature
              // must match the participant's PROFILE NAME, so signing is
              // impossible until the profile is complete (a name is set). Rather
              // than the old "couldn't be loaded — try again" (which reads as a
              // transient glitch), GATE signing behind profile completion: an
              // honest message + a direct route to the profile step. The Sign &
              // submit CTA is already disabled here (signOk=false, expected='').
              <div data-testid="waiver-profile-gate">
                <p role="alert" style={{ ...introStyle, color: 'var(--warn)', fontWeight: 700 }}>
                  {t('waiver.profileGate.note')}
                </p>
                <button
                  data-testid="waiver-complete-profile"
                  // Thread `?event=` (P2-3a, adversarial review): this detour
                  // is ALWAYS for THIS event's waiver, and the route's eventId
                  // is in scope — without it a shirt-off event's participant
                  // got the 5-step default wizard instead of the event-scoped
                  // one. Guarded: no fabricated id if the param were empty.
                  onClick={() => navigate(
                    eventId ? `/get-ready/profile?event=${encodeURIComponent(eventId)}` : '/get-ready/profile',
                    { state: { ticketId: ticketPassId, eventName: template.event } },
                  )}
                  style={ctaStyle(true)}
                >
                  {t('waiver.profileGate.cta')}
                </button>
              </div>
            ) : (
              <>
                <div data-testid="waiver-signed-for" style={{ fontSize: 11.5, color: 'var(--text-muted)', marginBottom: 12 }}>{sc.signedFor}</div>
                <label style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)', display: 'block', marginBottom: 6 }}>{sc.label}</label>
                <input
                  data-testid="waiver-sign-input"
                  value={typed}
                  onChange={(e) => setTyped(e.target.value)}
                  placeholder={expected}
                  aria-label={sc.label}
                  style={signBoxStyle(hint.tone)}
                />
                <div data-testid="waiver-sign-hint" style={signHintStyle(hint.tone)}>{hint.text}</div>
                {submitError && (
                  <div role="alert" style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--error)', marginTop: 8 }}>{submitError}</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--text-dim)', lineHeight: 1.5, marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--hairline-07)' }}>
                  {t('waiver.sign.note')}
                </div>
              </>
            )}
          </div>
        )}

        {/* ---- RECEIPT — rendered from the STORED record only (§6) ---- */}
        {onReceipt && record && (
          <div id="waiver.receipt">
            <div style={{ textAlign: 'center', padding: '6px 0 4px' }}>
              <div style={{ width: 58, height: 58, borderRadius: '50%', background: 'rgba(18,196,172,.16)', color: 'var(--accent-bright)', display: 'grid', placeItems: 'center', fontSize: 27, margin: '0 auto 14px' }}>✓</div>
              <h1 data-testid="waiver-title" style={h1Style}>{t('waiver.receipt.title')}</h1>
              <p style={{ ...introStyle, margin: '0 0 18px' }}>{t('waiver.receipt.stamp', { when: record.when })}</p>
            </div>
            <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-08)', borderRadius: 14, padding: '4px 15px', marginBottom: 14 }}>
              {receiptRows(record, t).map((r) => (
                <div key={r.k} data-testid="waiver-receipt-row" data-k={r.k} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 0', borderBottom: '1px solid var(--hairline-06)' }}>
                  <span style={{ flex: 1, fontSize: 12.5, color: 'var(--text-muted)' }}>{r.k}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, textAlign: 'end', overflowWrap: 'anywhere' }}>{r.v}</span>
                </div>
              ))}
            </div>
            {/* README §C receipt actions: the context-aware next action (prior
                art, CMT-2026-08-13-121331), then `Re-read the agreement` +
                `Download signed copy (PDF)` as the design's outline pair. */}
            <div style={{ display: 'grid', gap: 9 }}>
              {nextAction && nextAction.kind !== 'pending' ? (
                <button
                  data-testid="waiver-next"
                  data-next-kind={nextAction.kind}
                  onClick={goNext}
                  style={{ ...ctaStyle(true), width: '100%' }}
                >
                  {nextAction.kind === 'ticket' ? t('waiver.receipt.viewTicket') : t('waiver.receipt.next', { label: nextAction.step.label })}
                </button>
              ) : (
                // No further user step and no ticket yet — honest waiting state,
                // no button (canon: never a fabricated/dead action).
                <div data-testid="waiver-next-pending" role="status" style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--text-muted)', textAlign: 'center', padding: '4px 8px' }}>
                  {t('waiver.receipt.pending')}
                </div>
              )}
              <button data-testid="waiver-reread" onClick={() => go('doc-1')} style={outlineBtnStyle}>{t('waiver.receipt.reread')}</button>
              <button data-testid="waiver-download" onClick={downloadSignedPdf} style={outlineBtnStyle}>{t('waiver.receipt.download')}</button>
            </div>
          </div>
        )}
      </div>

      {/* BLOCK BAR */}
      {showBlock && (
        <div id="waiver.block" data-testid="waiver-block" style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 8, padding: `10px ${blockPadX}px`, background: 'rgba(230,193,90,.1)', borderTop: '1px solid rgba(230,193,90,.28)' }}>
          <span style={{ flex: 'none', color: 'var(--warn)', fontSize: 13 }}>⚠</span>
          <span style={{ fontSize: 11.5, lineHeight: 1.45, color: 'var(--warn)', fontWeight: 700 }}>{blockText}</span>
        </div>
      )}

      {/* FOOTER — absent on the RECEIPT (BOARDROOM-2026-09-05-121609 item 7,
          Duncan ruling: ONE green per screen). The receipt's single primary is
          the context-aware NEXT action mid-page; the sidebar Get-ready item +
          the head chevron are the sanctioned exits, so the old full-width
          `Back to Get ready` bar is gone entirely. */}
      {!onReceipt && (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'center', gap: 10, padding: footPad, borderTop: '1px solid var(--hairline-07)', background: 'var(--bg-frame)' }}>
          <button data-testid="waiver-back" onClick={back} style={backBtnStyle}>{t('flow.back')}</button>
          {onDoc && signed && docIndex === NDOCS - 1 ? (
            // Read-only re-read of the last doc returns to the receipt (§6).
            <button data-testid="waiver-continue" onClick={() => go('receipt')} style={ctaStyle(true)}>{t('waiver.backToReceipt')}</button>
          ) : onDoc ? (
            <button data-testid="waiver-continue" onClick={continueDoc} aria-disabled={!curDocOk} style={ctaStyle(curDocOk)}>{t('flow.continue')}</button>
          ) : (
            <button
              data-testid="waiver-submit"
              onClick={() => { void submit() }}
              aria-disabled={!signOk || submitState === 'submitting'}
              style={ctaStyle(signOk && submitState !== 'submitting')}
            >
              {submitState === 'submitting' ? t('waiver.sign.busy') : t('waiver.sign.cta')}
            </button>
          )}
        </div>
      )}
    </div>
  )
}
